

import { 
  collection, 
  addDoc, 
  updateDoc,
  doc, 
  serverTimestamp,
  deleteField,
  runTransaction,
  Firestore,
} from "firebase/firestore";
import { CampOrganization } from "./campOrganizationTypes";
import { getCampOrganization } from "./campOrganizationQuery";
import { createLogger } from "@/utils/logUtils";

const logger = createLogger("campOrganizationCreateUpdate");

// Helper
const getCampOrganizationsRef = (db: Firestore) => collection(db, "campOrganizations");

// CREATE
export const createCampOrganization = async (
  db: Firestore,
  organizationData: Omit<CampOrganization, "id" | "createdAt" | "lastEditedAt">
) => {
  try {
    const organizationsRef = getCampOrganizationsRef(db);
    
    // Create a proper document reference for lastEditedBy if it's a string
    let lastEditedBy = organizationData.lastEditedBy;
    if (typeof lastEditedBy === 'string') {
      lastEditedBy = doc(db, "users", lastEditedBy);
    }
    
    const newOrg = {
      ...organizationData,
      lastEditedBy,
      createdAt: serverTimestamp(),
      lastEditedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(organizationsRef, newOrg);
    logger.info("Camp Organization created with ID:", docRef.id);

    return { 
      id: docRef.id, 
      ...organizationData,
      createdAt: new Date().toISOString(),
      lastEditedAt: new Date().toISOString()
    };
  } catch (error) {
    logger.error("Error creating camp organization:", error);
    throw error;
  }
};

// UPDATE
const buildCampOrganizationUpdate = (
  db: Firestore,
  data: Partial<CampOrganization>,
  userId: string
): Record<string, any> => {
    // Process data removing undefined values, handling null appropriately for different field types
    const processedData: Record<string, any> = {};
    const booleanFields = ['isNonProfit', 'isFranchise', 'isMunicipalGovt'];
    const numericFields = ['yearOrgFounded', 'yearDoingCampSince'];
    
    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined) return;

      if (key === "canonicalLocationId") {
        if (value === null || value === "") {
          processedData[key] = deleteField();
        } else if (typeof value === "string") {
          const t = value.trim();
          processedData[key] = t.length ? t : deleteField();
        }
        return;
      }
      
      // For boolean fields, allow null values (represents "Not Set")
      if (booleanFields.includes(key)) {
        processedData[key] = value; // Can be true, false, or null
      } else if (numericFields.includes(key)) {
        // For numeric fields, allow null (represents intentional deletion by user)
        processedData[key] = value; // Can be number or null
      } else if (value === null) {
        return; // Skip null values for other non-boolean, non-numeric fields
      } else if (key === 'mailingAddress' && value) {
        const address: Record<string, any> = {};
        const mailingAddressObj = value as Record<string, any>;
        Object.entries(mailingAddressObj).forEach(([addrKey, addrValue]) => {
          if (addrValue !== undefined && addrValue !== null) address[addrKey] = addrValue;
        });
        if (Object.keys(address).length > 0) processedData[key] = address;
      } else {
        // Special handling for description fields - explicit logging to track values
        if (key === 'descAboutTheOrg' || key === 'descStaffingAndLeadership') {
          logger.info(`Processing ${key}, value length: ${typeof value === 'string' ? value.length : 'not a string'}, first chars: ${typeof value === 'string' ? value.substring(0, 20) : 'N/A'}`);
        }
        processedData[key] = value;
      }
    });

    processedData.lastEditedAt = serverTimestamp();
    
    // Handle userId - it could be a token ID or a regular user ID
    if (userId.startsWith('token-')) {
      // For token users, just store the token ID as a string
      processedData.lastEditedBy = userId;
    } else {
      // For regular users, convert to a document reference
      processedData.lastEditedBy = doc(db, "users", userId);
    }

    return processedData;
};

const notifyAndRefetchCampOrganization = async (db: Firestore, id: string) => {
    // Dispatch an event to notify that org data has been updated
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('organizationDataSaved', {
        detail: { organizationId: id }
      }));
    }

    const updatedOrg = await getCampOrganization(db, id);
    return updatedOrg;
};

export const updateCampOrganization = async (
  db: Firestore,
  id: string,
  data: Partial<CampOrganization>,
  userId: string
) => {
  try {
    const orgRef = doc(db, "campOrganizations", id);

    logger.info(`Starting update for camp organization ${id} with userId ${userId}`);
    logger.debug("Update data received:", data);

    const processedData = buildCampOrganizationUpdate(db, data, userId);

    logger.info(`Updating camp organization ${id} with data:`, processedData);

    await updateDoc(orgRef, processedData);
    logger.info(`Camp Organization ${id} updated successfully`);

    return await notifyAndRefetchCampOrganization(db, id);
  } catch (error) {
    logger.error("Error updating camp organization:", error);
    throw error;
  }
};

/**
 * updateCampOrganization for a patch derived from the org's current data: `derive` gets the data read
 * inside the transaction, so a write committed meanwhile (a director's do-not-contact click) makes
 * the transaction retry on fresh data instead of being overwritten. `derive` may run more than once.
 */
export const updateCampOrganizationInTransaction = async (
  db: Firestore,
  id: string,
  derive: (current: Record<string, unknown>) => Partial<CampOrganization>,
  userId: string
) => {
  try {
    const orgRef = doc(db, "campOrganizations", id);

    logger.info(`Starting transactional update for camp organization ${id} with userId ${userId}`);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(orgRef);
      if (!snap.exists()) throw new Error(`campOrganizations/${id} not found`);
      const data = derive(snap.data() as Record<string, unknown>);
      logger.debug("Update data derived:", data);
      tx.update(orgRef, buildCampOrganizationUpdate(db, data, userId));
    });
    logger.info(`Camp Organization ${id} updated successfully`);

    return await notifyAndRefetchCampOrganization(db, id);
  } catch (error) {
    logger.error("Error updating camp organization:", error);
    throw error;
  }
};

// Lightweight visibility-only update that does NOT touch lastEditedAt/lastEditedBy
export const updateCampOrganizationVisibility = async (
  db: Firestore,
  id: string,
  visibility: 'active' | 'archived'
) => {
  try {
    const orgRef = doc(db, 'campOrganizations', id);
    await updateDoc(orgRef, { visibility });
    return true;
  } catch (error) {
    logger.error('Error updating camp organization visibility:', error);
    throw error;
  }
};

